import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useApi from 'hooks/useApi';
import cardsFactory from 'utils/factories/card';
import marketPlaceFactory from 'utils/factories/marketplace';

type useCardOfferListApiProps = {
  cardRef: string;
  itemsPerPage?: number;
  page?: number;
  disabled?: boolean;
};
export const useCardOfferListApi = ({ cardRef, itemsPerPage, page, disabled }: useCardOfferListApiProps) => {
  const apiClient = useApi();

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.list({ cardRef, itemsPerPage: itemsPerPage || null, page: page || null }),
    queryFn: async () => apiClient.cards.apiCardsReferenceoffersGetCollection(cardRef, { itemsPerPage, page }),
    enabled: !disabled,
  });

  return { queryResult };
};

type useCardOfferDetailApiProps = {
  cardRef: string;
  offerId?: string;
};

export const useCardOfferDetailApi = ({ cardRef, offerId }: useCardOfferDetailApiProps) => {
  const apiClient = useApi();

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.details(offerId!, { cardRef }),
    queryFn: async () => apiClient.cards.apiCardsReferenceoffersOfferIdGetCollection(cardRef, offerId!),
    enabled: !!offerId,
  });

  return { queryResult };
};

export const useEditOffers = (onSuccess: (message: string) => void, onError: (error: Error) => void) => {
  const queryClient = useQueryClient();
  const apiClient = useApi();
  const updateOffer = useMutation({
    mutationKey: ['editOffer'],
    mutationFn: ({ price, cardRef, quantity }: { price: number; cardRef: string; quantity: number }) =>
      apiClient.marketplace.apiMarketplaceReferencePut(cardRef, { newPrice: price, quantity }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardsFactory.all() });
      queryClient.invalidateQueries({ queryKey: marketPlaceFactory.all() });

      if (onSuccess) {
        onSuccess('edit-offer-marketplace-success');
      }
    },
    onError,
  });

  return { updateOffer };
};

export const useAddOffers = (onSuccess: (message: string) => void, onError: (error: Error) => void) => {
  const queryClient = useQueryClient();
  const apiClient = useApi();
  const addOffer = useMutation({
    mutationFn: apiClient.marketplace.apiMarketplaceaddPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardsFactory.all() });
      queryClient.invalidateQueries({ queryKey: marketPlaceFactory.all() });

      if (onSuccess) {
        onSuccess('publish-marketplace-success');
      }
    },
    onError,
  });

  return { addOffer };
};

export const useRemoveOffers = (onSuccess: (message: string) => void, onError: (error: Error) => void) => {
  const queryClient = useQueryClient();
  const apiClient = useApi();
  const removeOffer = useMutation({
    mutationKey: ['removeOffer'],
    mutationFn: ({ cardRef }: { cardRef: string }) => apiClient.marketplace.apiMarketplaceReferenceDelete(cardRef),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardsFactory.all() });
      queryClient.invalidateQueries({ queryKey: marketPlaceFactory.all() });
      if (onSuccess) {
        onSuccess('remove-offer-marketplace-success');
      }
    },
    onError,
  });

  return { removeOffer };
};

export const useBuyOfferApi = (onSuccess: (message: string) => void) => {
  const queryClient = useQueryClient();
  const apiClient = useApi();

  const buyOffer = useMutation({
    mutationKey: ['buyOffer'],
    mutationFn: (newCart: { quantity?: number; cardRef?: string; offerId?: string }) =>
      apiClient.marketplace.apiMarketplaceordercreatePost(newCart),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cardsFactory.all() });
      queryClient.invalidateQueries({ queryKey: marketPlaceFactory.all() });
      onSuccess('buy-offer-marketplace-success');
    },
  });

  return { buyOffer };
};

export const usePaymentPricingGridApi = (amount?: number) => {
  const apiClient = useApi();

  const queryParams = { amount: amount?.toString() };

  return useQuery({
    queryKey: marketPlaceFactory.pricingGrid(queryParams),
    queryFn: async () => {
      const res = await apiClient.payments.apiPaymentspricingGridGetCollection(queryParams);

      return res.data;
    },
  });
};
